import React, { useEffect } from 'react';
import {
    DeleteInviteForAccountingArea,
    FetchInvitesForAccountingArea,
    UserSetupProcess,
    UserSetupProcesses,
    UserSetupProcessStatus,
} from '../../store/accountingAreaRolesSlice';
import LargeCard from '../main/large-card';
import LinkButton from '../util/link-button';
import { RootState } from '../../store';
import { connect } from 'react-redux';
import useTranslate from '../useTranslate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router';
import { ManagedPartnerDto } from '../../store/partnerManagerSlice';
import useFeatureAccess from '../../util/useFeatureAccess';

export interface Props {
    FetchInvitesForAccountingArea: () => void;
    DeleteInviteForAccountingArea: (processId: number | string) => void;
    usersInActivation?: UserSetupProcesses;
    selectedPartner: ManagedPartnerDto;
}

function UserActivationOverview(props: Readonly<Props>) {
    const strings = useTranslate('UserActivationOverview');
    const processes = props.usersInActivation?.setup_processes ?? [];
    const { canWriteFeature } = useFeatureAccess({
        feature_access: props.selectedPartner?.feature_access,
    });
    const hasWriteInvite = canWriteFeature('GENERAL_ACCESS_MANAGEMENT');
    useEffect(() => {
        props.FetchInvitesForAccountingArea();
    }, []);

    function handleDeleteClicked(process: UserSetupProcess) {
        if (
            window.confirm(
                strings._utils.sprintf(
                    strings.confirmDeleteRoleMessage,
                    process.email
                )
            )
        ) {
            props.DeleteInviteForAccountingArea(process.id);
        }
    }

    function combineRoleAndSubRole(user: UserSetupProcess): string {
        const roleString = strings.roleTranslations[user.role];
        if (user.sub_role) {
            return `${roleString} (${strings.subRoleTranslations[user.sub_role]})`;
        } else {
            return roleString;
        }
    }

    return (
        <LargeCard title={strings.tableTitle}>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>{strings.userEmailHeader}</th>
                        <th>{strings.roleHeader}</th>
                        <th>{strings.statusHeader}</th>
                        <th></th>
                    </tr>
                </thead>
                {processes?.map((userSetupProcess) => {
                    return (
                        <tr key={userSetupProcess.id}>
                            <td>{userSetupProcess.email}</td>
                            <td>{combineRoleAndSubRole(userSetupProcess)}</td>
                            <td>
                                {userSetupProcess.status ===
                                UserSetupProcessStatus.COMPLETED ? (
                                    <FontAwesomeIcon
                                        color={'green'}
                                        icon={faCheckCircle}
                                    />
                                ) : (
                                    strings.statusTranslations[
                                        userSetupProcess.status
                                    ]
                                )}
                            </td>
                            <td>
                                <Link
                                    to="/roles"
                                    onClick={(event) => {
                                        handleDeleteClicked(userSetupProcess);
                                    }}
                                    className="icon icon-wew-trash icon-sm"
                                ></Link>
                            </td>
                        </tr>
                    );
                })}
            </table>
            {hasWriteInvite && (
                <LinkButton
                    text={strings.toAddUserForm}
                    to="/roles/invitation"
                    pullRight
                    marginTop="0.5em"
                />
            )}
        </LargeCard>
    );
}

export default connect(
    (store: RootState) => ({
        usersInActivation: store.accountingAreaRoles.invitesForAccountingArea,
        selectedPartner: store.partnerManager.selectedPartner,
    }),
    {
        FetchInvitesForAccountingArea,
        DeleteInviteForAccountingArea,
    }
)(UserActivationOverview);
